import React from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';

import useLocales from 'hooks/use-locales';
import ErrorBoundary from 'hoc/ErrorBoundary';
import LoadingPage from 'pages/LoadingPage';
import PlannerError from 'pages/organization/OrganizationWeekPlannerPage/components/planner-error';
import JobDetailsError from 'pages/organization/OrganisationJobDetail/components/job-details-error';
import OrganizationPeriodJobsError from 'pages/organization/OrganisationPeriodJobsPage/components/organization-period-jobs-error';
import FreelancerAvailableJobs from 'pages/freelancer/FreelancerAvailableJobs';
import FreelancerJobsError from 'pages/freelancer/FreelancerAvailableJobs/components/freelancer-jobs-error';
import FreelancerPeriodJobs from 'pages/freelancer/FreelancerPeriodJobs';
import FreelancerPeriodJobsError from 'pages/freelancer/FreelancerPeriodJobs/components/freelancer-period-jobs-error';
import FreelancerAvailabilityPage from 'pages/freelancer/FreelancerAvailabilityPage';
import FreelancerAvailabilityErrorPage from 'pages/freelancer/FreelancerAvailabilityPage/components/freelancer-availability-error-page';
import FreelancerSignUpPage from 'pages/freelancer/FreelancerSignUpPage';
import useGoogleTagManager from 'hooks/use-google-tag-manager';

import client from 'state/remote/client';
import FreelancerAvailaleJobsContext from 'state/context/freelancer/FreelancerAvailableJobs';
import FreelancerPeriodJobsContext from 'state/context/freelancer/FreelancerPeriodJobs';
import OrganisationContext from 'state/context/OrganisationContext';
import OrganisationJobDetail from 'state/context/organization/OrganisationJobDetail';
import OrganizationWeekPlannerContext from 'state/context/organization/OrganizationWeekPlanner';
import IntegrationSettingsContext from 'state/context/organization/OrganisationSettings/IntegrationSettingsContext';
import OrganisationSettingsError from 'pages/organization/OrganisationSettingsPage/components/organisation-settings-error';

const OrganizationWeekPlannerPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "test" */ 'pages/organization/OrganizationWeekPlannerPage'
    ),
);
const OrganizationJobDetail = React.lazy(
  () => import('pages/organization/OrganisationJobDetail'),
);

const OrganisationIntegrationSettings = React.lazy(
  () =>
    import('pages/organization/OrganisationSettingsPage/integration-settings'),
);

const OrganisationPeriodJobsPage = React.lazy(
  () => import('pages/organization/OrganisationPeriodJobsPage'),
);

const App: React.FC = () => {
  const { initialize: initializeGoogleTagManager } = useGoogleTagManager();

  initializeGoogleTagManager();
  useLocales();

  return (
    <BrowserRouter>
      <QueryClientProvider client={client}>
        <Routes>
          <Route
            path="/organisation/planner/"
            element={
              <OrganizationWeekPlannerContext.Provider>
                <OrganisationContext.Provider>
                  <ErrorBoundary fallback={<PlannerError />}>
                    <React.Suspense fallback={<LoadingPage />}>
                      <OrganizationWeekPlannerPage />
                    </React.Suspense>
                  </ErrorBoundary>
                </OrganisationContext.Provider>
              </OrganizationWeekPlannerContext.Provider>
            }
          />
          <Route
            path="organisation/period_jobs/"
            element={
              <OrganisationContext.Provider>
                <ErrorBoundary fallback={<OrganizationPeriodJobsError />}>
                  <React.Suspense fallback={<LoadingPage />}>
                    <OrganisationPeriodJobsPage />
                  </React.Suspense>
                </ErrorBoundary>
              </OrganisationContext.Provider>
            }
          />
          <Route
            path="/organisation/planner/jobs/:id/"
            element={
              <OrganisationContext.Provider>
                <OrganisationJobDetail.Provider>
                  <ErrorBoundary fallback={<JobDetailsError />}>
                    <React.Suspense fallback={<LoadingPage />}>
                      <OrganizationJobDetail />
                    </React.Suspense>
                  </ErrorBoundary>
                </OrganisationJobDetail.Provider>
              </OrganisationContext.Provider>
            }
          />
          <Route
            path="/organisation/settings/integrations/*"
            element={
              <IntegrationSettingsContext.Provider>
                <OrganisationContext.Provider>
                  <ErrorBoundary fallback={<OrganisationSettingsError />}>
                    <React.Suspense fallback={<LoadingPage />}>
                      <OrganisationIntegrationSettings />
                    </React.Suspense>
                  </ErrorBoundary>
                </OrganisationContext.Provider>
              </IntegrationSettingsContext.Provider>
            }
          />

          <Route
            path="/freelancer/ads/all/"
            element={
              <FreelancerAvailaleJobsContext.Provider>
                <ErrorBoundary fallback={<FreelancerJobsError />}>
                  <React.Suspense fallback={<LoadingPage />}>
                    <FreelancerAvailableJobs />
                  </React.Suspense>
                </ErrorBoundary>
              </FreelancerAvailaleJobsContext.Provider>
            }
          />

          <Route
            path="/freelancer/ads/period/"
            element={
              <FreelancerPeriodJobsContext.Provider>
                <ErrorBoundary fallback={<FreelancerPeriodJobsError />}>
                  <React.Suspense fallback={<LoadingPage />}>
                    <FreelancerPeriodJobs />
                  </React.Suspense>
                </ErrorBoundary>
              </FreelancerPeriodJobsContext.Provider>
            }
          />
          <Route
            path="/freelancer/availability/"
            element={
              <ErrorBoundary fallback={<FreelancerAvailabilityErrorPage />}>
                <React.Suspense fallback={<LoadingPage />}>
                  <FreelancerAvailabilityPage />
                </React.Suspense>
              </ErrorBoundary>
            }
          />
          <Route path="/*" element={<FreelancerSignUpPage />} />
        </Routes>
      </QueryClientProvider>
      <ToastContainer />
    </BrowserRouter>
  );
};

export default App;
